import { retrieveRawInitData } from "@tma.js/sdk-react"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { useCallback, useEffect, useRef, useState } from "react"
import { setReauthHandler } from "@/shared/api/authenticated-client"
import { AUTH_ERROR_MESSAGES } from "@/shared/config/auth"
import { isDevMode } from "@/shared/lib/env"
import type { User } from "@/shared/lib/types"
import { useTelegramAuth } from "../api/auth"
import { generateMockInitData } from "../lib/mock-init-data"

const REAUTH_ATTEMPT_LIMIT = 3

export const useAuth = () => {
	const [isAuthenticating, setIsAuthenticating] = useState(true)
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [user, setUser] = useState<User | null>(null)
	const [error, setError] = useState<string | null>(null)
	const { mutateAsync: authenticate } = useTelegramAuth()
	const reauthCountRef = useRef(0)

	const initAuth = useCallback(async () => {
		setIsAuthenticating(true)
		setIsAuthenticated(false)
		try {
			let initData: string | undefined

			if (isDevMode()) {
				initData = generateMockInitData()
			} else {
				initData = retrieveRawInitData()
			}

			if (!initData) {
				setError(AUTH_ERROR_MESSAGES.NO_INIT_DATA)
				return
			}

			const response = await authenticate(initData)
			if (response.user) {
				setUser(response.user)
				setIsAuthenticated(true)
				setError(null)
				reauthCountRef.current = 0
			}
		} catch (authError) {
			setError(extractApiErrorMessage(authError, AUTH_ERROR_MESSAGES.AUTH_FAILED))
			setIsAuthenticated(false)
		} finally {
			setIsAuthenticating(false)
		}
	}, [authenticate])

	useEffect(() => {
		initAuth()
	}, [initAuth])

	useEffect(() => {
		setReauthHandler(() => {
			if (reauthCountRef.current >= REAUTH_ATTEMPT_LIMIT) {
				setIsAuthenticating(false)
				setIsAuthenticated(false)
				setError(AUTH_ERROR_MESSAGES.AUTH_FAILED)
				return
			}
			reauthCountRef.current += 1
			initAuth()
		})
	}, [initAuth])

	return {
		isAuthenticating,
		isAuthenticated,
		user,
		error,
	}
}
